import { auth, API_BASE } from "./firebase";

/** Call a session-authenticated backend endpoint with the Firebase ID token. */
async function call<T>(path: string, body?: unknown): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error("not signed in");
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: body ? "POST" : "GET",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data as T;
}

export interface TopupOrder {
  order_id: string;
  amount_paise: number;
  currency: string;
  key_id: string;
}

/**
 * Fetch (and server-side ensure) the signed-in user's wallet. The first call
 * for a brand-new account creates the wallet and seeds the free welcome pages,
 * so the dashboard's live Firestore listener has something to show. Call once
 * after sign-in; the listener takes over from there.
 */
export const ensureWalletWeb = () =>
  call<{ balance_paise: number; pages_remaining: number }>("/web/wallet");

export const createTopup = (amount_paise: number) =>
  call<TopupOrder>("/web/topup", { amount_paise });

export interface RazorpaySuccess {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

/** Credit the wallet synchronously after Checkout succeeds (webhook is failsafe). */
export const verifyTopup = (p: RazorpaySuccess) =>
  call<{ balance_paise: number }>("/web/topup/verify", p);

/**
 * Send the passwordless sign-in email from OUR backend (Admin SDK generates the
 * link; we send a branded email from no-reply@pdf.kras.ai). Better deliverability
 * and formatting than Firebase's default template. Opt-in via
 * VITE_CUSTOM_SIGNIN_EMAIL=true once the backend route exists — see emails/README.md.
 * Unauthenticated: no ID token is available before sign-in.
 */
export const sendSignInLink = async (email: string, continue_url: string): Promise<{ ok: boolean }> => {
  const res = await fetch(`${API_BASE}/web/auth/send-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, continue_url }),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data as { ok: boolean };
};

export const approveDevice = (user_code: string) =>
  call<{ ok: boolean; device_name: string }>("/web/devices/approve", { user_code });

export const revokeDevice = (id: string) =>
  call<{ ok: boolean }>(`/web/devices/${id}/revoke`, {});

// ---- admin (requires the admin custom claim) -------------------------------
export interface AdminUser {
  uid: string;
  email: string;
  wallet: { balance_paise: number; price_per_page_paise: number; pages_remaining: number };
  recent_jobs: Array<{ id: string; mode: string; status: string; pages: number; cost_paise: number; created_at: string | null }>;
}

export const adminSetPricing = (price_per_page_paise: number) =>
  call<{ ok: boolean; price_per_page_paise: number }>("/admin/pricing", { price_per_page_paise });

export const adminCredit = (uid: string, amount_paise: number, note: string) =>
  call<{ ok: boolean; uid: string; balance_paise: number }>("/admin/credit", { uid, amount_paise, note });

export const adminLookup = (email: string) =>
  call<AdminUser>(`/admin/lookup?email=${encodeURIComponent(email)}`);
